import { Link } from "react-router-dom";
import { Star, User, DollarSign, Clock, ArrowRight, Sparkles, Phone } from "lucide-react";
import { BRAND } from "@/config/brand";

const C = BRAND.colors;
const teal = "#14B8A6";
const gold = "#FFD700";
const bloodRed = "#DC2626";

// Real market data — the 4 competitors from the sourced comparison + PlugitAI
// as the 5th "we're here" card. Numbers are what these agencies actually quote
// on Clutch / DesignRush for equivalent-scope small-business web builds.
const COMPETITORS = [
  {
    name: "Bop Design",
    logo: "BOP",
    logoBg: "#111",
    logoColor: "#FFD100",
    rating: 5.0,
    reviews: 127,
    team: "10 – 49",
    hourly: "$150 – $199 / hr",
    project: "$10,000 – $49,000",
    href: null,
  },
  {
    name: "ControlF5",
    logo: "Ctrl F5",
    logoBg: "#F5F5FA",
    logoColor: "#2B2BBE",
    rating: 4.9,
    reviews: 183,
    team: "50 – 249",
    hourly: "$100 – $149 / hr",
    project: "Less than $10,000",
    href: null,
  },
  {
    name: "UPQODE",
    logo: "UPQODE",
    logoBg: "#111",
    logoColor: "#FFFFFF",
    rating: 4.9,
    reviews: 104,
    team: "50 – 249",
    hourly: "$100 – $149 / hr",
    project: "$10,000 – $49,000",
    href: null,
  },
  {
    name: "PopArt Studio",
    logo: "🖇",
    logoBg: "#FFFFFF",
    logoColor: "#111",
    rating: 5.0,
    reviews: 96,
    team: "50 – 249",
    hourly: "$50 – $99 / hr",
    project: "$10,000 – $49,000",
    href: null,
  },
];

// The star row used on both agency cards and PlugitAI's card
function Stars({ rating, reviews, colored = bloodRed }) {
  const rounded = Math.round(rating * 10) / 10;
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-bold text-sm" style={{ color: colored }}>{rounded.toFixed(1)}</span>
      <div className="flex">
        {[0, 1, 2, 3, 4].map((i) => (
          <Star key={i} size={12} fill={colored} color={colored} strokeWidth={0} />
        ))}
      </div>
      <span className="text-xs" style={{ color: colored }}>({reviews})</span>
    </div>
  );
}

function AgencyCard({ agency }) {
  return (
    <div
      className="relative flex flex-col rounded-2xl overflow-hidden border transition-transform hover:-translate-y-0.5"
      style={{
        background: "#FFFFFF",
        borderColor: "#E5E7EB",
        boxShadow: "0 12px 30px -20px rgba(0,0,0,0.15)",
      }}
      data-testid={`agency-${agency.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      {/* Logo tile */}
      <div className="flex justify-center pt-6 pb-2">
        <div
          className="w-14 h-14 rounded-md flex items-center justify-center font-black"
          style={{
            background: agency.logoBg,
            color: agency.logoColor,
            fontSize: agency.logo.length > 4 ? 10 : 13,
            letterSpacing: "0.02em",
            border: agency.logoBg === "#FFFFFF" ? "1px solid #E5E7EB" : "none",
          }}
        >
          {agency.logo}
        </div>
      </div>

      <div className="px-5 pb-5">
        <h4 className="text-center text-xl font-black" style={{ color: "#111827" }}>
          {agency.name}
        </h4>
        <div className="mt-2 flex justify-center">
          <Stars rating={agency.rating} reviews={agency.reviews} />
        </div>

        <div className="mt-4 flex justify-center items-center gap-1.5 text-sm" style={{ color: "#374151" }}>
          <User size={14} /> <span>{agency.team}</span>
        </div>

        {/* Pricing band */}
        <div className="mt-5 -mx-5 px-5 py-4 space-y-2" style={{ background: "#F3F4F6" }}>
          <div className="flex items-center gap-2 text-sm font-bold" style={{ color: "#111827" }}>
            <Clock size={14} /> {agency.hourly}
          </div>
          <div className="flex items-center gap-2 text-sm font-bold" style={{ color: "#111827" }}>
            <DollarSign size={14} /> {agency.project}
          </div>
        </div>

        <div
          className="mt-5 w-full h-11 rounded-md flex items-center justify-center px-2 text-[10px] uppercase tracking-[0.22em] font-black text-center leading-tight"
          style={{
            background: "#111827",
            color: "#FFFFFF",
            border: `1px solid ${gold}`,
            boxShadow: `inset 0 0 20px rgba(255,215,0,0.18)`,
          }}
          data-testid={`agency-callout-${agency.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
        >
          From us · same site from{" "}
          <span className="ml-1 font-black" style={{ color: gold }}>$299.99</span>
        </div>
      </div>
    </div>
  );
}

// The 5th card — PlugitAI, styled to visually stomp the competitors
function PlugitAICard() {
  return (
    <div
      className="relative flex flex-col rounded-2xl overflow-hidden border-2 transition-transform hover:-translate-y-1"
      style={{
        background:
          "linear-gradient(180deg, #0B1524 0%, #060C18 100%)",
        borderColor: gold,
        boxShadow: `0 30px 80px -20px ${gold}66, 0 20px 50px -20px ${teal}55, inset 0 1px 0 rgba(255,255,255,0.05)`,
      }}
      data-testid="agency-plugitai"
    >
      {/* "You're here" ribbon */}
      <div
        className="absolute top-0 left-0 right-0 py-1.5 text-center text-[9px] font-black uppercase tracking-[0.28em]"
        style={{
          background: `linear-gradient(90deg, ${gold} 0%, #FFA500 100%)`,
          color: "#000",
          letterSpacing: "0.28em",
        }}
      >
        ✦ You just found us ✦
      </div>

      {/* Logo tile */}
      <div className="flex justify-center pt-10 pb-2">
        <img
          src={BRAND.logos.plugitai}
          alt="PlugitAI"
          className="h-14 w-auto"
          style={{ filter: `drop-shadow(0 6px 20px ${teal}88)` }}
        />
      </div>

      <div className="px-5 pb-5">
        <h4 className="text-center text-xl font-black text-white">PlugitAI</h4>
        <div className="mt-2 flex justify-center">
          <Stars rating={5.0} reviews={"real·live"} colored={gold} />
        </div>

        <div className="mt-4 flex justify-center items-center gap-1.5 text-sm" style={{ color: C.textDim }}>
          <User size={14} /> <span>Team of 4 · in-house</span>
        </div>

        {/* Pricing band */}
        <div
          className="mt-5 -mx-5 px-5 py-4 space-y-2 border-y"
          style={{ background: `${gold}0e`, borderColor: `${gold}44` }}
        >
          <div className="flex items-center gap-2 text-sm font-bold text-white">
            <Clock size={14} style={{ color: gold }} /> $0 / hr — no clock
          </div>
          <div className="flex items-center gap-2 text-sm font-black" style={{ color: gold }}>
            <DollarSign size={14} /> $600 – $2,000 flat
          </div>
        </div>

        <a
          href="tel:+12148155966"
          className="mt-5 w-full h-11 rounded-md font-black text-sm text-white inline-flex items-center justify-center gap-2"
          style={{
            background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
            boxShadow: `0 12px 30px -12px ${teal}aa`,
          }}
          data-testid="plugitai-card-cta"
        >
          <Phone size={13} /> Get a quote
        </a>
      </div>
    </div>
  );
}

export default function IsWebsiteRightForYou() {
  return (
    <section
      className="relative z-10 py-20 md:py-28"
      style={{ background: "transparent" }}
      data-testid="is-website-right"
    >
      {/* Ambient glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background: `radial-gradient(1000px 500px at 50% 20%, ${gold}14, transparent 60%),
                       radial-gradient(800px 400px at 20% 90%, ${teal}14, transparent 65%)`,
        }}
      />

      <div className="mx-auto max-w-[1280px] px-6 md:px-10 relative">
        {/* Header */}
        <div className="text-center mb-10 md:mb-14">
          <div
            className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.28em]"
            style={{
              background: `${gold}18`,
              color: gold,
              border: `1px solid ${gold}55`,
            }}
          >
            <Sparkles size={11} />
            Yes — we do websites
          </div>
          <h2
            className="mt-5 font-black tracking-[-0.02em] leading-[0.96]"
            style={{ fontSize: "clamp(2.2rem, 5.6vw, 4.6rem)", color: "white" }}
            data-testid="is-website-right-h1"
          >
            Find out if a{" "}
            <span
              style={{
                backgroundImage: `linear-gradient(135deg, ${gold} 0%, #FFA500 40%, ${teal} 100%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                filter: `drop-shadow(0 0 26px ${gold}66)`,
              }}
            >
              new website
            </span>{" "}
            is right for you.
          </h2>
          <p
            className="mt-5 max-w-[720px] mx-auto text-base md:text-lg leading-relaxed"
            style={{ color: C.textDim }}
          >
            Here&apos;s what the market charges. Then look at what{" "}
            <strong style={{ color: gold }}>we</strong> charge. Same result. Same 5-star work.
            Fraction of the price. Do the math for yourself.
          </p>
        </div>

        {/* Comparison grid: 4 competitors + PlugitAI */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5" data-testid="comparison-grid">
          {COMPETITORS.map((a) => (
            <AgencyCard key={a.name} agency={a} />
          ))}
          <PlugitAICard />
        </div>

        {/* Payoff punchline */}
        <div
          className="mt-10 md:mt-14 rounded-3xl border p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-5 md:gap-8"
          style={{
            background:
              "linear-gradient(135deg, rgba(255,215,0,0.10) 0%, rgba(20,184,166,0.10) 100%), rgba(11,21,36,0.72)",
            borderColor: `${gold}55`,
            backdropFilter: "blur(10px)",
            boxShadow: `0 40px 120px -50px ${gold}55`,
          }}
          data-testid="is-website-right-payoff"
        >
          <div className="shrink-0">
            <div className="text-[10px] uppercase tracking-[0.28em] font-black" style={{ color: gold }}>
              The math
            </div>
            <div
              className="mt-1 font-black leading-none tracking-[-0.02em]"
              style={{ fontSize: "clamp(2.4rem, 5vw, 4rem)", color: "white" }}
            >
              1<span style={{ color: gold }}>/</span>25<span className="text-white/60 text-2xl align-top ml-1">th</span>
            </div>
            <div className="text-sm mt-1" style={{ color: C.textDim }}>
              the price of the &ldquo;5-star agency&rdquo; next to us.
            </div>
          </div>

          <div className="flex-1 min-w-0">
            <div className="font-black leading-tight text-white" style={{ fontSize: "clamp(1.2rem, 2.4vw, 1.75rem)" }}>
              You don&apos;t need a $25,000 agency. You need a team of 4 that actually picks up the phone.
            </div>
            <div className="mt-2 text-sm leading-relaxed" style={{ color: C.textDim }}>
              Same 100 pages of exclusive content. Same 24/7 AI receptionist. Same
              local SEO across every city you serve. None of the retainer trap.
            </div>
          </div>

          <div className="flex flex-wrap gap-3 md:justify-end">
            <a
              href="tel:+12148155966"
              className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-black text-sm text-white"
              style={{
                background: `linear-gradient(135deg, ${C.blueBright} 0%, ${teal} 100%)`,
                boxShadow: `0 20px 50px -20px ${teal}aa`,
              }}
              data-testid="is-website-right-cta-call"
            >
              <Phone size={14} /> (214) 815-5966 <ArrowRight size={14} />
            </a>
            <Link
              to="/case-studies/miss-rodriguez"
              className="inline-flex items-center gap-2 px-5 h-12 rounded-full font-semibold text-sm border"
              style={{ borderColor: `${gold}88`, color: "white" }}
              data-testid="is-website-right-cta-proof"
            >
              See a $2,000 site in action
            </Link>
          </div>
        </div>

        {/* Fine print */}
        <p
          className="mt-6 text-[11px] text-center max-w-[820px] mx-auto"
          style={{ color: C.textFade }}
        >
          Competitor pricing sourced from public Clutch / DesignRush listings. Bop Design, ControlF5,
          UPQODE, and PopArt Studio are not affiliated with PlugitAI. Comparison shown for
          transparency — not for shade. They&apos;re great teams. We&apos;re just a much smaller,
          faster, cheaper one.
        </p>
      </div>
    </section>
  );
}
